import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './AddRechnungWizard.css';
import './SendEmailWizard.css';
import { RoundTable } from './wizardTypes';
import EmailWizardStepIndicator from './EmailWizardStepIndicator';
import EmailWizardStep1 from './EmailWizardStep1';
import EmailWizardStep2 from './EmailWizardStep2';
import EmailWizardStep3 from './EmailWizardStep3';

interface SendEmailWizardProps {
    projectId: number;
    projectName: string;
    onClose: () => void;
    token: string;
}

interface Rechnung {
    id: number;
    roundTable?: {
        id: number;
        name: string;
        number: number;
    };
}

interface RoundTableWithInvoice extends RoundTable {
    rechnungId: number;
    selected: boolean;
}

const SendEmailWizard: React.FC<SendEmailWizardProps> = ({
    projectId,
    projectName,
    onClose,
    token,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState<string | null>(null);

    // Step 1: Table Selection
    const [roundTablesWithInvoices, setRoundTablesWithInvoices] = useState<RoundTableWithInvoice[]>([]);
    const [loadingTables, setLoadingTables] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Step 2: Email Composition
    const [subject, setSubject] = useState('');
    const [cc, setCc] = useState('');
    const [content, setContent] = useState('');
    const [attachInvoice, setAttachInvoice] = useState(true);

    // Fetch round tables with invoices
    useEffect(() => {
        const fetchRoundTablesWithInvoices = async () => {
            try {
                // First fetch all round tables
                const rtResponse = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!rtResponse.ok) {
                    throw new Error('Failed to fetch round tables');
                }

                const allRoundTables: RoundTable[] = await rtResponse.json();

                // Then fetch invoices for this project
                const rechnungenResponse = await fetch(`/api/rechnungen/project/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!rechnungenResponse.ok) {
                    throw new Error('Failed to fetch invoices');
                }

                const rechnungen: Rechnung[] = await rechnungenResponse.json();

                // Filter round tables that have invoices and map them
                // Use a Map to avoid duplicates if multiple invoices exist for the same round table
                const tablesMap = new Map<number, RoundTableWithInvoice>();
                
                rechnungen.forEach(rechnung => {
                    if (rechnung.roundTable) {
                        const roundTableId = rechnung.roundTable.id;
                        // Only add if not already in the map
                        if (!tablesMap.has(roundTableId)) {
                            const fullTable = allRoundTables.find(rt => rt.id === roundTableId);
                            if (fullTable) {
                                tablesMap.set(roundTableId, {
                                    ...fullTable,
                                    rechnungId: rechnung.id,
                                    selected: true // All selected by default
                                });
                            }
                        }
                    }
                });

                setRoundTablesWithInvoices(Array.from(tablesMap.values()));
            } catch (error) {
                console.error('Error fetching round tables with invoices:', error);
                toast.error('Fehler beim Laden der Round Tables');
            } finally {
                setLoadingTables(false);
            }
        };

        fetchRoundTablesWithInvoices();
    }, [token, projectId]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const toggleTableSelection = (tableId: number) => {
        setRoundTablesWithInvoices(tables =>
            tables.map(t => t.id === tableId ? { ...t, selected: !t.selected } : t)
        );
    };

    const handleNext = () => {
        setError(null);

        if (currentStep === 1) {
            const selectedCount = roundTablesWithInvoices.filter(t => t.selected).length;
            if (selectedCount === 0) {
                setError('Bitte wählen Sie mindestens einen Round Table aus');
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (!subject.trim()) {
                setError('Bitte geben Sie einen Betreff ein');
                return;
            }
            if (!content.trim()) {
                setError('Bitte geben Sie einen Inhalt ein');
                return;
            }
            setCurrentStep(3);
        }
    };

    const handleBack = () => {
        setError(null);
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const handleSendTest = () => {
        toast.info('Noch nicht implementiert');
        onClose();
    };

    const handleSkipTest = () => {
        toast.info('Noch nicht implementiert');
        onClose();
    };

    const insertPlaceholder = (placeholder: string) => {
        setContent(prev => prev + placeholder);
    };

    return (
        <div className="wizard-overlay" onClick={handleOverlayClick}>
            <div className="wizard-modal wizard-modal-large">
                <div className="wizard-header">
                    <h2>E-Mails versenden</h2>
                    <button
                        className="wizard-close-btn"
                        onClick={onClose}
                        aria-label="Schließen"
                    >
                        ✕
                    </button>
                </div>

                <div className="wizard-content">
                    <div className="wizard-info">
                        <p className="project-info">
                            <strong>Projekt:</strong> {projectName}
                        </p>
                    </div>

                    <EmailWizardStepIndicator currentStep={currentStep} />

                    <div className="wizard-steps">
                        {currentStep === 1 && (
                            <EmailWizardStep1
                                roundTablesWithInvoices={roundTablesWithInvoices}
                                loadingTables={loadingTables}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                toggleTableSelection={toggleTableSelection}
                            />
                        )}
                        {currentStep === 2 && (
                            <EmailWizardStep2
                                subject={subject}
                                setSubject={setSubject}
                                cc={cc}
                                setCc={setCc}
                                content={content}
                                setContent={setContent}
                                attachInvoice={attachInvoice}
                                setAttachInvoice={setAttachInvoice}
                                insertPlaceholder={insertPlaceholder}
                            />
                        )}
                        {currentStep === 3 && (
                            <EmailWizardStep3
                                roundTablesWithInvoices={roundTablesWithInvoices}
                                subject={subject}
                                cc={cc}
                                content={content}
                                attachInvoice={attachInvoice}
                                onSendTest={handleSendTest}
                                onSkipTest={handleSkipTest}
                            />
                        )}
                    </div>

                    {error && (
                        <div className="wizard-error">
                            {error}
                        </div>
                    )}

                    <div className="wizard-actions">
                        <button
                            type="button"
                            className="wizard-btn wizard-btn-cancel"
                            onClick={onClose}
                        >
                            Abbrechen
                        </button>
                        {currentStep > 1 && currentStep < 3 && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-back"
                                onClick={handleBack}
                            >
                                Zurück
                            </button>
                        )}
                        {currentStep < 3 && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-submit"
                                onClick={handleNext}
                            >
                                Weiter
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendEmailWizard;
